import { type ComponentType, memo, useMemo } from "react";
import { Marker } from "react-map-gl/mapbox";

import { AnchoredMapPopup } from "@/components/Map/AnchoredMapPopup";
import {
	MarkerPopupArrow,
	type MarkerPopupArrow as MarkerPopupArrowValue,
} from "@/components/Map/MarkerPopup";
import { POPUP_PLACEMENT_WRAPPER_CLASS } from "@/features/map/lib/resolve-popup-placement";
import { getEntityIdFromMapFeature } from "@/utils/map/get-entity-id-from-feature";
import { isClusterMapFeature } from "@/utils/map/pick-primary-map-feature";

import type {
	MapDataLayerTooltipProps,
	MapGeoJSONFeature,
	MapPopupTapOverride,
} from "./MapBoxComponent.types";

export interface MapFeaturePopupInfo {
	feature: MapGeoJSONFeature;
	layerId: string;
	layerLabel: string;
	longitude: number;
	latitude: number;
	tapLongitude?: number;
	tapLatitude?: number;
	/** True when the open click hit an MVT vertex circle layer. */
	isVertexLayerHit?: boolean;
	/**
	 * Snapped vertex coordinate when {@link isVertexLayerHit} — drives the
	 * active-vertex overlay circle on the map.
	 */
	activeVertex?: { longitude: number; latitude: number } | null;
	TooltipComponent: ComponentType<MapDataLayerTooltipProps>;
	/** Caret direction from resolvePopupPlacement. */
	arrow?: MarkerPopupArrowValue;
	/** Tip-centric wrapper classes from resolvePopupPlacement. */
	wrapperClassName?: string;
}

interface MapFeaturePopupProps {
	popupInfo: MapFeaturePopupInfo;
	onClose: () => void;
	popupTapOverride?: MapPopupTapOverride | null;
}

/**
 * Destiny tip-centric popup at a lat/lng origin (same model as MapShell markers).
 *
 * Uses a 0×0 Marker + {@link AnchoredMapPopup} so caret tips land on the
 * feature anchor; Mapbox's default popup tip/offset is not used.
 */
export const MapFeaturePopup = memo(function MapFeaturePopup({
	popupInfo,
	onClose,
	popupTapOverride = null,
}: MapFeaturePopupProps) {
	const { TooltipComponent } = popupInfo;

	const isCluster = isClusterMapFeature(
		popupInfo.feature.properties as Record<string, unknown> | undefined,
	);
	const pointCount = popupInfo.feature.properties?.point_count as
		| number
		| undefined;

	const arrow = popupInfo.arrow ?? MarkerPopupArrow.BottomMiddle;
	const wrapperClassName =
		popupInfo.wrapperClassName ??
		POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.BottomMiddle];

	const tooltipProps = useMemo((): MapDataLayerTooltipProps => {
		const popupEntityId = getEntityIdFromMapFeature(popupInfo.feature);
		const hasTapOverride =
			popupTapOverride != null &&
			popupInfo.layerId === popupTapOverride.layerId &&
			popupEntityId != null &&
			popupEntityId === popupTapOverride.entityId;
		return {
			feature: popupInfo.feature,
			layerId: popupInfo.layerId,
			layerLabel: popupInfo.layerLabel,
			isCluster,
			pointCount,
			longitude: popupInfo.longitude,
			latitude: popupInfo.latitude,
			tapLongitude: hasTapOverride
				? popupTapOverride.tapLongitude
				: popupInfo.tapLongitude,
			tapLatitude: hasTapOverride
				? popupTapOverride.tapLatitude
				: popupInfo.tapLatitude,
			isVertexLayerHit: popupInfo.isVertexLayerHit === true,
			onClose,
			arrow,
		};
	}, [
		popupInfo.feature,
		popupInfo.layerId,
		popupInfo.layerLabel,
		popupInfo.longitude,
		popupInfo.latitude,
		popupInfo.tapLongitude,
		popupInfo.tapLatitude,
		popupInfo.isVertexLayerHit,
		popupTapOverride,
		isCluster,
		pointCount,
		onClose,
		arrow,
	]);

	return (
		<Marker
			longitude={popupInfo.longitude}
			latitude={popupInfo.latitude}
			anchor="top-left"
			style={{ width: 0, height: 0, zIndex: 40 }}
		>
			<div className="relative size-0 overflow-visible">
				<AnchoredMapPopup wrapperClassName={wrapperClassName}>
					<TooltipComponent {...tooltipProps} />
				</AnchoredMapPopup>
			</div>
		</Marker>
	);
});
